import { api } from '../../../shared/lib/axios';

/** Alocação em cliente e o lado do cliente nas férias (change `pessoas-ferias-gestor-cliente`). */

export interface Allocation {
  id: string;
  bondId: string;
  internalRegistration: string;
  client: { id: string; code: string; name: string };
  project: { id: string; name: string } | null;
  professionalProfile: { id: string; name: string } | null;
  clientManager: { id: string; name: string; active: boolean };
  validFrom: string;
  validTo: string | null;
  requiresSubstitute: boolean;
  suspendAccess: boolean;
  accessNotes: string | null;
  endedReason: string | null;
}

export interface AllocationPayload {
  clientId?: string;
  projectId?: string;
  professionalProfileId?: string | null;
  clientManagerUserId?: string;
  validFrom?: string;
  validTo?: string;
  requiresSubstitute?: boolean;
  suspendAccess?: boolean;
  accessNotes?: string | null;
}

export interface AllocationOptions {
  projects: Array<{ id: string; code: string; name: string }>;
  managers: Array<{ id: string; name: string; email: string }>;
  profiles: Array<{ id: string; name: string }>;
}

export interface ClientCoverage {
  id: string;
  allocationId: string;
  clientName: string;
  projectName: string | null;
  clientManagerName: string | null;
  needsSubstitute: boolean;
  substitute: { id: string; name: string } | null;
  acknowledgedAt: string | null;
  acknowledgedByName: string | null;
  suspendAccess: boolean;
  feedback: Array<{ kind: 'ciencia' | 'conflito'; justification: string | null; createdAt: string; userName: string | null }>;
  accessTasks: Array<{ kind: 'suspensao' | 'reativacao'; status: string; dueDate: string; notes: string | null }>;
}

export interface AccessTask {
  id: string;
  kind: 'suspensao' | 'reativacao';
  status: 'pendente' | 'concluida' | 'falha' | 'excecao' | 'cancelada';
  dueDate: string;
  notes: string | null;
  doneAt: string | null;
  doneByName: string | null;
  vacation: { requestId: string; startDate: string; endDate: string };
  userId: string;
  personName: string;
  clientName: string;
  projectName: string | null;
  accessNotes: string | null;
}

export interface PortalVacation {
  coverageId: string | null;
  status: string;
  statusLabel: string;
  totalDays: number;
  installments: Array<{ startDate: string; endDate: string; returnDate: string; days: number; access: string; accessLabel: string }>;
  needsSubstitute: boolean;
  substitute: { name: string; role: string | null } | null;
  acknowledgedAt: string | null;
  conflicts: Array<{ justification: string; createdAt: string }>;
}

export interface PortalProfessional {
  allocationId: string;
  userId: string;
  name: string;
  role: string | null;
  project: string | null;
  internalManager: string | null;
  allocation: { validFrom: string; validTo: string | null };
  status: string | null;
  statusLabel: string | null;
  hasAvailableVacation: boolean;
  periodsAvailable: number;
  suspendAccess: boolean;
  vacations: PortalVacation[];
}

function list<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[];
  const data = (body as { data?: unknown } | null)?.data;
  return Array.isArray(data) ? (data as T[]) : [];
}

function object<T>(body: unknown): T {
  const { version: _v, _links: _l, ...rest } = (body ?? {}) as Record<string, unknown>;
  return rest as T;
}

export async function alocacoesDaPessoa(personId: string): Promise<Allocation[]> {
  const { data } = await api.get(`/collaborators/${personId}/allocations`);
  return list<Allocation>(data);
}

export async function clientesParaAlocacao(): Promise<Array<{ id: string; code: string; name: string }>> {
  const { data } = await api.get('/client-allocations/clients');
  return list(data);
}

export async function opcoesDeAlocacao(clientId: string): Promise<AllocationOptions> {
  const { data } = await api.get(`/clients/${clientId}/allocation-options`);
  return object<AllocationOptions>(data);
}

export async function alocar(bondId: string, payload: AllocationPayload): Promise<Allocation> {
  const { data } = await api.post(`/employment-bonds/${bondId}/allocations`, payload);
  return object<Allocation>(data);
}

export async function atualizarAlocacao(id: string, payload: AllocationPayload): Promise<Allocation> {
  const { data } = await api.patch(`/client-allocations/${id}`, payload);
  return object<Allocation>(data);
}

export async function encerrarAlocacao(id: string, validTo: string, reason: string): Promise<Allocation> {
  const { data } = await api.post(`/client-allocations/${id}/end`, { validTo, reason });
  return object<Allocation>(data);
}

export async function coberturasDasFerias(requestId: string): Promise<ClientCoverage[]> {
  const { data } = await api.get(`/vacation-requests/${requestId}/client-coverages`);
  return list<ClientCoverage>(data);
}

export async function atualizarCobertura(id: string, payload: { needsSubstitute?: boolean; substituteUserId?: string | null }) {
  const { data } = await api.patch(`/vacation-client-coverages/${id}`, payload);
  return object<{ id: string }>(data);
}

export async function tarefasDeAcesso(scope: 'abertas' | 'todas'): Promise<AccessTask[]> {
  const { data } = await api.get('/vacation-access-tasks', { params: { scope } });
  return list<AccessTask>(data);
}

export async function agirNaTarefaDeAcesso(id: string, action: 'concluir' | 'falha' | 'excecao', notes?: string) {
  const { data } = await api.post(`/vacation-access-tasks/${id}`, { action, notes });
  return object<{ id: string }>(data);
}

export async function feriasDaEquipeDoCliente(): Promise<PortalProfessional[]> {
  const { data } = await api.get('/client-portal/team-vacations');
  return list<PortalProfessional>(data);
}

export async function darCiencia(coverageId: string) {
  const { data } = await api.post(`/client-portal/vacation-coverages/${coverageId}/acknowledge`);
  return object<{ id: string }>(data);
}

export async function manifestarConflito(coverageId: string, justification: string) {
  const { data } = await api.post(`/client-portal/vacation-coverages/${coverageId}/conflict`, { justification });
  return object<{ id: string }>(data);
}
